import { Button, InputBox } from '@imprivata-cloud/components';
import type { FormInstance } from 'antd';
import { Form } from 'antd';
import type React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { featureSupported } from '../../featureFlagsUtils';
import { Regex } from '../../utils/utils';
import { Trans, useTranslation } from 'react-i18next';
import { STEPS } from '../../setup/constants';
import { useNavigateToNextStep } from '../../setup/hooks/useNavigateToNextStep';
import CannotCompleteSetupModal from '../cannotCompleteModal/CannotCompleteSetupModal';
import CharacterCountHelper from '../characterCountHelper/CharacterCountHelper';
import CopyButton from '../copyButton/copyButton';
import './IdpInfoForm.less';
import type { IdpMetadataInfo } from '../../api/types';

const TRANSLATION_ROOT = `setup.${STEPS.EXTERNAL_IDP_INFO}.content`;
const FILE_INPUT_ID = 'xml-file-input';
const INPUT_CLASSNAME = 'impr-idp-input';

export enum IdpFormFieldNames {
  DOMAIN = 'IdPDomain',
  NAME = 'IdPName',
  URL = 'IdPURL',
  XML = 'IdPXML',
  GROUPS_NAME = 'AdminGroupsName',
  GROUPS_VALUES = 'AdminGroupsValues',
  ADMIN_EMAIL_LOCAL = 'AdminEmailLocalPart',
}

export interface FormValues {
  IdPDomain: string;
  IdPName: string;
  IdPURL: string;
  IdPXML: string;
  AdminGroupsName?: string;
  AdminGroupsValues?: string;
  AdminEmailLocalPart?: string;
}

const IdpInfoForm = ({
  form,
  modalOnClose,
  spMetadataURL,
  onFinish,
  isLoading,
  isSetupSkipAllowed,
  isSubmitBlocked = false,
  setupExistingIdpMetadata,
  editMode = false,
  onFileReadError,
  savedDomain,
  requiresAdminEmail,
}: {
  form: FormInstance;
  // Mandatory for portal. This way we know this is portal, not setup.
  modalOnClose?: () => void;
  spMetadataURL: string;
  onFinish: (values: FormValues) => void;
  isLoading?: boolean;
  isSetupSkipAllowed?: boolean; // Mandatory for setup
  isSubmitBlocked?: boolean;
  setupExistingIdpMetadata?: IdpMetadataInfo;
  editMode?: boolean; // Optional, only for portal
  onFileReadError?: (error: unknown) => void;
  savedDomain?: string;
  requiresAdminEmail?: boolean;
}) => {
  const isPortal = !!modalOnClose;
  const isSetupRerun = !!setupExistingIdpMetadata;
  const { t } = useTranslation();
  const navigateToNextStep = useNavigateToNextStep();

  const [fileName, setFileName] = useState<string>('');
  const [cannotCompleteModalVisible, setCannotCompleteModalVisible] = useState(false);

  const urlValue = Form.useWatch(IdpFormFieldNames.URL, form);
  const currentDomain = Form.useWatch(IdpFormFieldNames.DOMAIN, form);
  const currentName = Form.useWatch(IdpFormFieldNames.NAME, form);
  const domainChanged = isPortal && normalizeDomain(currentDomain) !== normalizeDomain(savedDomain);
  const showAdminEmail =
    featureSupported('EMAIL_VERIFICATION_ENABLED') && (isPortal ? domainChanged : !!requiresAdminEmail);
  const hasExistingMetadataUrl = !!setupExistingIdpMetadata?.metadataUrl;
  const isXmlFieldDisabled = !editMode && !!(urlValue || (isSetupRerun && hasExistingMetadataUrl));

  const handleInputBoxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value;
    if (inputValue === '') {
      form.setFieldsValue({ IdPXML: '' });
      setFileName('');
    } else {
      setFileName(inputValue);
    }
  };

  const handleInputBoxFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.select();
  };

  const handleInputBoxKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Tab') {
      return;
    }
    event.preventDefault();
    if (event.key === 'Backspace' || event.key === 'Delete') {
      form.setFieldsValue({ IdPXML: '' });
      setFileName('');
    }
  };

  const handleSkip = () => {
    if (isSetupSkipAllowed) {
      navigateToNextStep(STEPS.SETUP_COMPLETE);
    } else {
      setCannotCompleteModalVisible(true);
    }
  };

  const handleFileInputChange = useCallback(
    (
      event: Event,
      resolve: (name: string, idpXml: string) => void,
      reject: (error: unknown) => void,
    ) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];

      if (!file) {
        reject(new Error('No file selected.'));
        return;
      }

      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (fileExtension === 'xml') {
        const reader = new FileReader();

        reader.onerror = e => {
          console.log('Failed to read the file. Please try again.');
          reject(e);
        };

        reader.onload = () => {
          try {
            const text = reader.result as string;
            const encoder = new TextEncoder();
            const data = encoder.encode(text);
            const idpXml = btoa(String.fromCharCode(...new Uint8Array(data)));

            resolve(file.name, idpXml);
          } catch (error) {
            console.log('There was an issue processing the file. Please try again.');
            reject(error);
          }
        };

        reader.readAsText(file, 'UTF-8');
      } else {
        console.log('Please select an XML file.');
        reject(new Error('Please select an XML file.'));
      }
    },
    [],
  );

  useEffect(() => {
    const fileInput = getFileInput();
    // Handle the file input change
    fileInput.onchange = event => {
      // Update the file name for display
      const resolve = (name: string, idpXml: string) => {
        form.setFieldsValue({ IdPXML: idpXml });
        setFileName(name);
      };

      const reject = (e: unknown) => {
        if (onFileReadError) {
          onFileReadError(e);
        } else {
          console.log('Error:', e);
        }
      };
      handleFileInputChange(event, resolve, reject);
    };
  }, [form.setFieldsValue, handleFileInputChange, onFileReadError]);

  const metaLabel = (
    <div className="idp-meta-label">
      <Trans t={t} i18nKey={`${TRANSLATION_ROOT}.idp-meta-label`} />
    </div>
  );

  return (
    <div className="idp-form-container">
      <div className={'idp-subtitle'}>{t(`${TRANSLATION_ROOT}.subtitle`)}</div>
      {!isPortal && metaLabel}
      <Form name="IdpInfo" form={form} layout="vertical" requiredMark={false} onFinish={onFinish}>
        <div className="row">
          <div>
            {/* Business Domain */}
            <Form.Item
              label={t(`${TRANSLATION_ROOT}.idp-domain-input`)}
              name={IdpFormFieldNames.DOMAIN}
              className={!isPortal ? 'idp-domain-form-item' : undefined}
              rules={[
                {
                  required: true,
                  message: t(`${TRANSLATION_ROOT}.validation.business-domain-required`),
                },
                { pattern: Regex.samlDomain, message: t(`${TRANSLATION_ROOT}.validation.domain`) },
                ...lengthRules(isPortal, 3, 64, t(`${TRANSLATION_ROOT}.validation.domain-length`)),
              ]}
            >
              <InputBox
                autoFocus
                size="large"
                data-testid="idp-domain-input"
                placeholder="yourorganization.com"
                {...lengthLimits(isPortal, 3, 64)}
              />
            </Form.Item>
            {!isPortal && (
              <CharacterCountHelper
                className="idp-domain-helper"
                text={t(`${TRANSLATION_ROOT}.idp-domain-helper`, {
                  count: (currentDomain ?? '').length,
                })}
              />
            )}
          </div>

          {/* Display Name */}
          <div>
            <Form.Item
              label={t(`${TRANSLATION_ROOT}.idp-name-input`)}
              name={IdpFormFieldNames.NAME}
              className={!isPortal ? 'idp-name-form-item' : undefined}
              rules={[
                {
                  required: true,
                  message: t(`${TRANSLATION_ROOT}.validation.display-name-required`),
                },
                ...lengthRules(isPortal, 6, 64, t(`${TRANSLATION_ROOT}.validation.display-name-length`)),
              ]}
            >
              <InputBox
                size="large"
                data-testid="idp-name-input"
                {...lengthLimits(isPortal, 6, 64)}
              />
            </Form.Item>
            {!isPortal && (
              <CharacterCountHelper
                className="idp-name-helper"
                text={t(`${TRANSLATION_ROOT}.idp-name-helper`, {
                  count: (currentName ?? '').length,
                })}
              />
            )}
          </div>
        </div>

        {showAdminEmail && (
        <div className={INPUT_CLASSNAME}>
          <Form.Item
            name={IdpFormFieldNames.ADMIN_EMAIL_LOCAL}
            label={t(`${TRANSLATION_ROOT}.verification-email-input`)}
            rules={[
              { required: true, message: t(`${TRANSLATION_ROOT}.validation.admin-email-required`) },
              { pattern: Regex.emailLocalPart, message: t(`${TRANSLATION_ROOT}.validation.admin-email`) },
            ]}
          >
            <InputBox data-testid="admin-email-input" size="large" />
          </Form.Item>
          <span className="impr-idp-input-suffix">{(currentDomain ? `@${currentDomain}` :  '')}</span>
        </div>
        )}

        {isPortal && metaLabel}

        {/* URL */}
        <Form.Item
          label={t(`${TRANSLATION_ROOT}.idp-url-input`)}
          name={'IdPURL'}
          rules={[{ type: 'url' }]}
          validateTrigger={['onBlur']}
        >
          <InputBox
            size="large"
            data-testid="idp-url-input"
            className={'input'}
            disabled={!!fileName}
            placeholder='https://'
          />
        </Form.Item>

        {/* IdP XML file */}
        <Form.Item label={t(`${TRANSLATION_ROOT}.idp-xml-input`)} name={'IdPXML'}>
          <div className="xml-form-item">
            <InputBox
              size="large"
              data-testid="idp-xml-input"
              className={'input'}
              value={fileName}
              onChange={handleInputBoxChange}
              onFocus={handleInputBoxFocus}
              onKeyDown={handleInputBoxKeyDown}
              readOnly={!fileName}
              disabled={isXmlFieldDisabled}
              placeholder={
                isSetupRerun && !hasExistingMetadataUrl
                  ? t(`${TRANSLATION_ROOT}.idp-xml-already-provided-hint`)
                  : undefined
              }
            />
            <Button
              type="primary"
              className={'browse-button'}
              label={t(`${TRANSLATION_ROOT}.idp-xml-browse-button`)}
              data-testid="idp-xml-browse-button"
              size="large"
              onClick={() => getFileInput().click()}
              disabled={isXmlFieldDisabled}
            />
          </div>
        </Form.Item>

        {/* Copy link */}
        <Form.Item label={t(`${TRANSLATION_ROOT}.sp-metadata-label`)} className="idp-sp-metadata-form-item">
          <div className="link-content">
            <InputBox
              data-testid="sp-metadata-input"
              className={'input'}
              value={spMetadataURL}
              disabled
            />
            <CopyButton
              textToCopy={spMetadataURL?.trim()}
              dynamicLabel={{
                initialLabel: t('common.copy'),
                labelAfterClick: t('common.copied'),
                dynamicLabelDuration: 5000,
              }}
            />
          </div>
        </Form.Item>
        {isPortal && (
          <>
            <Form.Item
              name="AdminGroupsName"
              label={t(`${TRANSLATION_ROOT}.admin-groups-name`)}
              rules={[{ required: true }]}
            >
              <InputBox />
            </Form.Item>
            <Form.Item
              name="AdminGroupsValues"
              label={t(`${TRANSLATION_ROOT}.admin-groups-values`)}
              rules={[{ required: true }]}
            >
              <InputBox />
            </Form.Item>
          </>
        )}
        {/* Submit button */}
        <div className={isPortal ? 'idp-form-button-container' : undefined}>
          <Form.Item shouldUpdate>
            {form => {
              const isSubmitEnabled = canSubmit(form, {
                isPortal,
                showAdminEmail,
                allowsEmptySource: isSetupRerun && !hasExistingMetadataUrl,
              });
              return (
                <div>
                  <Button
                    size="large"
                    type="primary"
                    block={!isPortal}
                    disabled={!isSubmitEnabled || isLoading || isSubmitBlocked}
                    htmlType="submit"
                    label={isPortal ? t('common.save') : t('common.continue-button')}
                  />
                </div>
              );
            }}
          </Form.Item>
          <Button
            label={isPortal ? t('common.cancel') : t('common.skip')}
            onClick={isPortal ? modalOnClose : handleSkip}
            size="large"
            className="idp-skip-button"
            style={{ boxShadow: 'none' }}
            block={!isPortal}
            data-testid="skip-button"
          />
        </div>
      </Form>
      <input id={FILE_INPUT_ID} type="file" accept=".xml" hidden />
      <CannotCompleteSetupModal
        isModalVisible={cannotCompleteModalVisible}
        setModalVisible={setCannotCompleteModalVisible}
      />
    </div>
  );
};

export default IdpInfoForm;

function normalizeDomain(domain?: string) {
  return (domain ?? '').toLowerCase().trim();
}

function lengthRules(isPortal: boolean, min: number, max: number, message: string) {
  return isPortal ? [] : [{ type: 'string' as const, min, max, message }];
}

function lengthLimits(isPortal: boolean, minLength: number, maxLength: number) {
  return isPortal ? {} : { minLength, maxLength };
}

function canSubmit(
  form: FormInstance,
  {
    isPortal,
    showAdminEmail,
    allowsEmptySource,
  }: { isPortal: boolean; showAdminEmail: boolean; allowsEmptySource: boolean },
) {
  const values = form.getFieldsValue() as FormValues;
  const hasErrors = form.getFieldsError().some(({ errors }) => errors.length > 0);
  const hasSource = isPortal || !!(values.IdPURL || values.IdPXML) || allowsEmptySource;
  const hasGroups = !isPortal || !!(values.AdminGroupsName && values.AdminGroupsValues);
  const hasAdminEmail = !showAdminEmail || !!values.AdminEmailLocalPart;
  const isTouched = !isPortal || form.isFieldsTouched();
  return (
    !!(values.IdPDomain && values.IdPName) &&
    hasSource &&
    hasGroups &&
    hasAdminEmail &&
    isTouched &&
    !hasErrors
  );
}

function getFileInput() {
  const fileInput = document.getElementById(FILE_INPUT_ID);
  // To stop IDE from complaining.
  if (!fileInput) {
    throw new Error('XML file input not found');
  }
  return fileInput;
}
